import Stripe from 'stripe';
import { StripeServerAPI } from '../api/stripe-server';
import { WEBHOOK_EVENTS } from '../../shared/constants';
import type {
  Request as ExpressRequest,
  Response as ExpressResponse,
} from 'express';
// Inline Next.js Pages Router types to avoid requiring `next` as a dependency.
// These match the subset used by createNextJSWebhookHandler (legacy).
interface NextApiRequest {
  method?: string;
  headers: Record<string, string | string[] | undefined>;
  body: any;
}
interface NextApiResponse {
  setHeader(name: string, value: string): void;
  status(code: number): NextApiResponse;
  json(body: unknown): void;
}

export interface WebhookHandlers {
  onCustomerSubscriptionCreated?: (
    subscription: Stripe.Subscription
  ) => Promise<void>;
  onCustomerSubscriptionUpdated?: (
    subscription: Stripe.Subscription
  ) => Promise<void>;
  onCustomerSubscriptionDeleted?: (
    subscription: Stripe.Subscription
  ) => Promise<void>;
  onInvoicePaymentSucceeded?: (invoice: Stripe.Invoice) => Promise<void>;
  onInvoicePaymentFailed?: (invoice: Stripe.Invoice) => Promise<void>;
  onCheckoutSessionCompleted?: (
    session: Stripe.Checkout.Session
  ) => Promise<void>;
  onPaymentIntentSucceeded?: (
    paymentIntent: Stripe.PaymentIntent
  ) => Promise<void>;
  onPaymentIntentPaymentFailed?: (
    paymentIntent: Stripe.PaymentIntent
  ) => Promise<void>;
}

export class StripeWebhookHandler {
  private stripeAPI: StripeServerAPI;
  private handlers: WebhookHandlers;

  constructor(secretKey: string, handlers: WebhookHandlers = {}) {
    this.stripeAPI = new StripeServerAPI(secretKey);
    this.handlers = handlers;
  }

  async handleWebhook(
    payload: string | Buffer,
    signature: string,
    webhookSecret: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const event = this.stripeAPI.constructWebhookEvent(
        payload,
        signature,
        webhookSecret
      );

      console.log(`Received webhook event: ${event.type}`);

      await this.processEvent(event);

      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown webhook error';
      console.error('Webhook error:', errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  private async processEvent(event: Stripe.Event): Promise<void> {
    switch (event.type) {
      case WEBHOOK_EVENTS.CUSTOMER_SUBSCRIPTION_CREATED:
        await this.handleSubscriptionCreated(
          event.data.object as Stripe.Subscription
        );
        break;

      case WEBHOOK_EVENTS.CUSTOMER_SUBSCRIPTION_UPDATED:
        await this.handleSubscriptionUpdated(
          event.data.object as Stripe.Subscription
        );
        break;

      case WEBHOOK_EVENTS.CUSTOMER_SUBSCRIPTION_DELETED:
        await this.handleSubscriptionDeleted(
          event.data.object as Stripe.Subscription
        );
        break;

      case WEBHOOK_EVENTS.INVOICE_PAYMENT_SUCCEEDED:
        await this.handleInvoicePaymentSucceeded(
          event.data.object as Stripe.Invoice
        );
        break;

      case WEBHOOK_EVENTS.INVOICE_PAYMENT_FAILED:
        await this.handleInvoicePaymentFailed(
          event.data.object as Stripe.Invoice
        );
        break;

      case WEBHOOK_EVENTS.CHECKOUT_SESSION_COMPLETED:
        await this.handleCheckoutSessionCompleted(
          event.data.object as Stripe.Checkout.Session
        );
        break;

      case WEBHOOK_EVENTS.PAYMENT_INTENT_SUCCEEDED:
        await this.handlePaymentIntentSucceeded(
          event.data.object as Stripe.PaymentIntent
        );
        break;

      case WEBHOOK_EVENTS.PAYMENT_INTENT_PAYMENT_FAILED:
        await this.handlePaymentIntentFailed(
          event.data.object as Stripe.PaymentIntent
        );
        break;

      default:
        console.log(`Unhandled event type: ${event.type}`);
    }
  }

  private async handleSubscriptionCreated(
    subscription: Stripe.Subscription
  ): Promise<void> {
    console.log(`Subscription created: ${subscription.id}`);

    if (this.handlers.onCustomerSubscriptionCreated) {
      await this.handlers.onCustomerSubscriptionCreated(subscription);
    }

    // Default behavior: Log subscription details

    console.log(
      `Customer ${subscription.customer} subscribed to ${subscription.items.data[0]?.price.id}`
    );
  }

  private async handleSubscriptionUpdated(
    subscription: Stripe.Subscription
  ): Promise<void> {
    console.log(`Subscription updated: ${subscription.id}`);

    if (this.handlers.onCustomerSubscriptionUpdated) {
      await this.handlers.onCustomerSubscriptionUpdated(subscription);
    }

    // Default behavior: Log status changes

    console.log(
      `Subscription ${subscription.id} status: ${subscription.status}`
    );
  }

  private async handleSubscriptionDeleted(
    subscription: Stripe.Subscription
  ): Promise<void> {
    console.log(`Subscription deleted: ${subscription.id}`);

    if (this.handlers.onCustomerSubscriptionDeleted) {
      await this.handlers.onCustomerSubscriptionDeleted(subscription);
    }

    // Default behavior: Log cancellation

    console.log(
      `Customer ${subscription.customer} cancelled subscription ${subscription.id}`
    );
  }

  private async handleInvoicePaymentSucceeded(
    invoice: Stripe.Invoice
  ): Promise<void> {
    console.log(`Invoice payment succeeded: ${invoice.id}`);

    if (this.handlers.onInvoicePaymentSucceeded) {
      await this.handlers.onInvoicePaymentSucceeded(invoice);
    }

    // Default behavior: Log successful payment
    const subscriptionId = (
      invoice as Stripe.Invoice & { subscription?: string }
    ).subscription;
    if (subscriptionId) {
      console.log(`Payment succeeded for subscription ${subscriptionId}`);
    }
  }

  private async handleInvoicePaymentFailed(
    invoice: Stripe.Invoice
  ): Promise<void> {
    console.log(`Invoice payment failed: ${invoice.id}`);

    if (this.handlers.onInvoicePaymentFailed) {
      await this.handlers.onInvoicePaymentFailed(invoice);
    }

    // Default behavior: Log failed payment
    const subscriptionId = (
      invoice as Stripe.Invoice & { subscription?: string }
    ).subscription;
    if (subscriptionId) {
      console.log(`Payment failed for subscription ${subscriptionId}`);
    }
  }

  private async handleCheckoutSessionCompleted(
    session: Stripe.Checkout.Session
  ): Promise<void> {
    console.log(`Checkout session completed: ${session.id}`);

    if (this.handlers.onCheckoutSessionCompleted) {
      await this.handlers.onCheckoutSessionCompleted(session);
    }

    // Default behavior: Log successful checkout
    if (session.mode === 'subscription') {
      console.log(
        `Subscription checkout completed for customer ${session.customer}`
      );
    } else {
      console.log(
        `Payment checkout completed for customer ${session.customer}`
      );
    }
  }

  private async handlePaymentIntentSucceeded(
    paymentIntent: Stripe.PaymentIntent
  ): Promise<void> {
    console.log(`Payment intent succeeded: ${paymentIntent.id}`);

    if (this.handlers.onPaymentIntentSucceeded) {
      await this.handlers.onPaymentIntentSucceeded(paymentIntent);
    }

    // Default behavior: Log successful payment

    console.log(
      `Payment of ${paymentIntent.amount} ${paymentIntent.currency} succeeded`
    );
  }

  private async handlePaymentIntentFailed(
    paymentIntent: Stripe.PaymentIntent
  ): Promise<void> {
    console.log(`Payment intent failed: ${paymentIntent.id}`);

    if (this.handlers.onPaymentIntentPaymentFailed) {
      await this.handlers.onPaymentIntentPaymentFailed(paymentIntent);
    }

    // Default behavior: Log failed payment

    console.log(
      `Payment of ${paymentIntent.amount} ${paymentIntent.currency} failed`
    );
  }
}

// Utility function to create a webhook handler with custom handlers
export const createStripeWebhookHandler = (
  secretKey: string,
  handlers: WebhookHandlers = {}
): StripeWebhookHandler => {
  return new StripeWebhookHandler(secretKey, handlers);
};

// Express.js middleware for handling Stripe webhooks
export const createStripeWebhookMiddleware = (
  secretKey: string,
  webhookSecret: string,
  handlers: WebhookHandlers = {}
) => {
  const webhookHandler = new StripeWebhookHandler(secretKey, handlers);

  return async (req: ExpressRequest, res: ExpressResponse) => {
    try {
      const signature = req.headers['stripe-signature'];

      if (!signature) {
        return res
          .status(400)
          .json({ error: 'Missing stripe-signature header' });
      }

      const sig = Array.isArray(signature) ? signature[0] : signature;
      if (!sig) {
        return res
          .status(400)
          .json({ error: 'Invalid stripe-signature header' });
      }

      const result = await webhookHandler.handleWebhook(
        req.body,
        sig,
        webhookSecret
      );

      if (result.success) {
        res.status(200).json({ received: true });
      } else {
        res.status(400).json({ error: result.error });
      }
    } catch (error) {
      console.error('Webhook middleware error:', error);
      res.status(500).json({ error: 'Internal server error' });
    }
  };
};

/**
 * Next.js App Router webhook handler (Next.js 13+ with `app/` directory).
 *
 * Returns a `POST` function suitable for use in `app/api/.../route.ts` files.
 * Uses the Web-standard `Request`/`Response` API instead of Pages Router types.
 *
 * @example
 * ```ts
 * // app/api/stripe-webhook/route.ts
 * import { createAppRouterWebhookHandler } from '@digilogiclabs/saas-factory-payments/server';
 *
 * export const POST = createAppRouterWebhookHandler(
 *   process.env.STRIPE_SECRET_KEY!,
 *   process.env.STRIPE_WEBHOOK_SECRET!,
 *   {
 *     onCheckoutSessionCompleted: async (session) => {
 *       if (session.metadata?.product_type === 'donation') {
 *         // Handle donation...
 *       }
 *     },
 *   },
 * );
 * ```
 */
export const createAppRouterWebhookHandler = (
  secretKey: string,
  webhookSecret: string,
  handlers: WebhookHandlers = {}
) => {
  const webhookHandler = new StripeWebhookHandler(secretKey, handlers);

  return async (request: Request): Promise<Response> => {
    try {
      const signature = request.headers.get('stripe-signature');

      if (!signature) {
        return Response.json(
          { error: 'Missing stripe-signature header' },
          { status: 400 }
        );
      }

      const rawBody = await request.text();

      const result = await webhookHandler.handleWebhook(
        rawBody,
        signature,
        webhookSecret
      );

      if (result.success) {
        return Response.json({ received: true });
      } else {
        return Response.json({ error: result.error }, { status: 400 });
      }
    } catch (error) {
      console.error('Webhook handler error:', error);
      return Response.json({ error: 'Internal server error' }, { status: 500 });
    }
  };
};

/** @deprecated Use `createAppRouterWebhookHandler` for Next.js 13+ App Router. */
// Next.js Pages Router API route handler (legacy)
export const createNextJSWebhookHandler = (
  secretKey: string,
  webhookSecret: string,
  handlers: WebhookHandlers = {}
) => {
  const webhookHandler = new StripeWebhookHandler(secretKey, handlers);

  return async (req: NextApiRequest, res: NextApiResponse) => {
    if (req.method !== 'POST') {
      res.setHeader('Allow', 'POST');
      return res.status(405).json({ error: 'Method not allowed' });
    }

    try {
      const signature = req.headers['stripe-signature'];

      if (!signature) {
        return res
          .status(400)
          .json({ error: 'Missing stripe-signature header' });
      }

      const sig = Array.isArray(signature) ? signature[0] : signature;
      if (!sig) {
        return res
          .status(400)
          .json({ error: 'Invalid stripe-signature header' });
      }

      const result = await webhookHandler.handleWebhook(
        req.body,
        sig,
        webhookSecret
      );

      if (result.success) {
        res.status(200).json({ received: true });
      } else {
        res.status(400).json({ error: result.error });
      }
    } catch (error) {
      console.error('Webhook handler error:', error);
      res.status(500).json({ error: 'Internal server error' });
    }
  };
};
